import React, { useRef, useState } from 'react';

import DOMPurify from 'isomorphic-dompurify';

import useOnClickOutside from '@components/common/hooks/useOnClickOutside';
import MoreIcon from '@components/common/MoreIcon/MoreIcon';
import Spinner from '@components/common/Spinner/Spinner';
import Text from '@components/common/Text/Text';
import MoreActions from '@components/lpc/GreetingCard/Message/MoreActions/MoreActions';
import CircleAvatar from '@components/lpc/Navbar/CircleAvatar/CircleAvatar';
import { personRoles } from '@models/PotModel/personRoles';
import { themeType } from '@propTypes/potTypes';
import { AvatarType } from '@propTypes/userTypes';

import css from './Message.module.scss';

type CardProps = {
    theme: themeType;
    message: string;
    firstName: string;
    lastName: string;
    signature: string;
    avatar: AvatarType;
    isLoading: boolean;
    onDelete?: () => void;
    personRole: string;
    onDeleteDisabled?: boolean;
    displayMoreActions?: boolean;
    onApplyAction?: (str: 'delete' | 'edit') => void;
};

const Message = ({
    theme,
    message,
    firstName,
    lastName,
    avatar,
    isLoading,
    onDelete,
    personRole,
    onDeleteDisabled,
    signature,
    displayMoreActions,
    onApplyAction,
}: CardProps) => {
    const [isHover, setIsHover] = useState(false);
    const socialRef = useRef();
    useOnClickOutside(socialRef, () => setIsPopUpOpen(false));

    const [isPopUpOpen, setIsPopUpOpen] = useState(false);

    return (
        <div
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            className={css.card}
            style={{ backgroundColor: theme.backgroundColorPrimary }}
        >
            {isLoading ? (
                <>
                    <Spinner />
                </>
            ) : (
                <>
                    <div
                        style={{ color: theme.textPrimaryVariant }}
                        className={css.card__message}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }}
                    />
                    {displayMoreActions && (
                        <div ref={socialRef}>
                            <MoreIcon
                                customClassContainer={css.more}
                                customClassIcon={css.notification__action__more__icon}
                                onOpen={() => setIsPopUpOpen(true)}
                                icon={'/front-static/icons/action/more.svg'}
                            />
                            <MoreActions isPopUpOpen={isPopUpOpen} onApplyAction={onApplyAction} />
                        </div>
                    )}

                    <section className={css.user}>
                        <CircleAvatar customClass={css.user__avatar} avatar={avatar} height={'24px'} width={'24px'} />

                        <Text variant={'caption_00'} color={theme.textPrimaryVariant}>
                            {signature || firstName + ' ' + lastName}
                        </Text>
                    </section>
                    {personRole === personRoles.ORGANISER && !onDeleteDisabled && (
                        <img
                            className={`${css.delete} ${isHover && css.delete__visible}`}
                            onClick={() => onDelete()}
                            src="/front-static/icons/action/delete.svg"
                            alt="delete"
                        />
                    )}
                </>
            )}
        </div>
    );
};
export default Message;
